import React, { useState } from 'react';
import { MessageCircle, Link2, Mail, Facebook } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Prominent "Share The Village" card — optimized for text-forwarding
 * which converts highest for women 50+. Prioritizes SMS + WhatsApp + Copy.
 *
 * Props:
 *   url      — URL to share (defaults to homepage)
 *   headline — large hero line
 *   body     — supporting sentence
 *   variant  — 'gold' (default) | 'cyan'
 */
export default function ShareVillageCard({
  url = 'https://thealchemyvillage.com',
  headline = 'Know a sister who needs this?',
  body = 'Send her The Village. One message could change her next chapter.',
  variant = 'gold',
}) {
  const [copied, setCopied] = useState(false);

  const message =
    'I found this place — The Alchemy Village. A space for women ready to stop sleep-walking through life and start becoming who they were meant to be. I think you\'d love it. → ' + url;

  const enc = encodeURIComponent;

  const handleSMS = () => {
    window.location.href = `sms:?&body=${enc(message)}`;
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/?text=${enc(message)}`, '_blank', 'noopener');
  };

  const handleEmail = () => {
    window.location.href = `mailto:?subject=${enc('Something I want you to see')}&body=${enc(message)}`;
  };

  const handleFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${enc(url)}&quote=${enc(headline)}`, '_blank', 'noopener');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      toast.success('Message copied. Paste it wherever you want.');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Copy failed. Long-press to copy.');
    }
  };

  const handleNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: headline, text: body, url });
        return true;
      } catch {
        /* dismissed */
      }
    }
    return false;
  };

  return (
    <div className={`svc svc--${variant}`} data-testid="share-village-card">
      <div className="svc-glow" aria-hidden="true" />
      <div className="svc-inner">
        <p className="svc-eyebrow">Pass it forward</p>
        <h3 className="svc-headline" data-testid="svc-headline">{headline}</h3>
        <p className="svc-body">{body}</p>

        <div className="svc-buttons">
          <button
            type="button"
            className="svc-btn svc-btn--primary"
            data-testid="svc-sms"
            onClick={async () => { if (!(await handleNative())) handleSMS(); }}
          >
            <MessageCircle size={18} strokeWidth={1.9} />
            <span>Text it to her</span>
          </button>
          <button
            type="button"
            className="svc-btn"
            data-testid="svc-whatsapp"
            onClick={handleWhatsApp}
          >
            <WhatsAppIcon size={18} />
            <span>WhatsApp</span>
          </button>
          <button
            type="button"
            className="svc-btn"
            data-testid="svc-email"
            onClick={handleEmail}
          >
            <Mail size={18} strokeWidth={1.9} />
            <span>Email</span>
          </button>
          <button
            type="button"
            className="svc-btn"
            data-testid="svc-facebook"
            onClick={handleFacebook}
          >
            <Facebook size={18} strokeWidth={1.9} />
            <span>Facebook</span>
          </button>
          <button
            type="button"
            className="svc-btn svc-btn--ghost"
            data-testid="svc-copy"
            onClick={handleCopy}
          >
            <Link2 size={18} strokeWidth={1.9} />
            <span>{copied ? 'Copied' : 'Copy message'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function WhatsAppIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 21l1.5-4.5A8.5 8.5 0 1 1 8 20.5L3 21z" />
      <path d="M8.5 9.5c0 3 2 5 5 5" />
      <path d="M9 8.5c.3-.5.8-.5 1.1 0 .2.3.4.7.6 1 .2.3 0 .6-.2.8l-.4.3c.4.7 1 1.3 1.7 1.7l.3-.4c.2-.2.5-.4.8-.2.3.2.7.4 1 .6.5.3.5.8 0 1.1-.8.5-1.8.4-2.8-.1" />
    </svg>
  );
}
